/**
 * English dictionary — source of truth. The type of this object defines the
 * shape every other locale must implement (`const ru: typeof en = …`), so a
 * missing key is a compile error, and the dot-path key type for `t()` is
 * derived from it.
 *
 * EN values are the strings that existed in the UI before i18n (verbatim).
 */
export const en = {
  common: {
    cancel: 'Cancel',
    create: 'Create',
    save: 'Save',
    done: 'Done',
    close: 'Close',
    details: 'Details',
    none: 'None',
    active: 'Active',
    inactive: 'Inactive',
    unassigned: 'Unassigned',
    noClient: 'No client',
    notAssigned: 'Not assigned',
  },
  roles: {
    manufacturer: 'Manufacturer',
    integrator: 'Integrator',
  },
  nav: {
    primary: 'Primary',
    dashboard: 'Dashboard',
    inquiries: 'Leads',
    clients: 'Clients',
    integrators: 'Integrators',
    bookings: 'Bookings',
    catalog: 'Catalog',
    orders: 'Orders',
    settings: 'Settings',
  },
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    subtitle: 'Robot Factory CRM',
    email: 'Email',
    password: 'Password',
    networkError: 'Network error — is the server running?',
  },
  status: {
    order: {
      DRAFT: 'Draft',
      CONFIRMED: 'Confirmed',
      IN_PRODUCTION: 'In production',
      SHIPPED: 'Shipped',
      SOLD: 'Sold',
      CANCELLED: 'Cancelled',
    },
  },
  stage: {
    validation: 'Incoming requests',
    verification: 'Verification',
    prioritization: 'Feasibility options',
    realization: 'Project concept',
    integratorSelection: 'Integrator selection',
    tkp: 'Commercial proposal',
    // Issue #80 — the industrial route's new step after the proposal.
    contract: 'Contract',
    // Educational robot: on the board the concept step already covers the
    // proposal, and the proposal step is effectively the contract.
    realizationEducational: 'Project concept / Commercial proposal',
    tkpEducational: 'Contract',
    won: 'Won',
    lost: 'Dropped',
    paused: 'Paused',
  },
  // Issue #52 — activity grouping («By activity») columns
  calendar: {
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    today: 'Today',
    noDate: 'No date: {count}',
  },
  activity: {
    overdue: 'Overdue',
    today: 'Today',
    thisWeek: 'This week',
    nextWeek: 'Next week',
    noDate: 'No date',
    later: 'More than two weeks out',
  },
  confidence: {
    k90: 'K90',
    k50: 'K50',
    k10: 'K10',
    unset: 'Not set',
    k90Label: 'Confident / Priority',
    k50Label: 'Base',
    k10Label: 'Reserve / Risky',
    reasonPrompt: 'Why this confidence rating?',
  },
  inquiryType: {
    question: 'Information request (question)',
    request: 'TOR submitted',
    invitation: 'Invitation',
    partnership: 'Partnership (joint order delivery)',
    saleOffer: 'Sales offer',
    gisp: 'GISP request',
    auditRequest: 'Audit request',
    solutionHelp: 'Help with solution selection',
  },
  transitions: {
    advance: 'Advance',
    pause: 'Pause',
    resume: 'Resume',
    // Issue #72 — the drawer action button says «Refusal»; statuses keep "Dropped".
    drop: 'Refusal',
    win: 'Mark won',
    setConfidence: 'Set confidence',
    reasonPrompt: 'Reason',
    reasonRequired: 'A reason is required for this action.',
    confidenceRequiredAdvance: 'A confidence rating is required to advance to {stage}.',
    chooseConfidence: 'Confidence rating',
    confidencePlaceholder: 'Select…',
    interactionModeLabel: 'Work mode',
    interactionModePlaceholder: 'Select…',
    modeAudit: 'Opportunity audit',
    modeRemoteTz: 'Proposal from TOR',
    auditDateLabel: 'Audit date',
    interactionModeRequired: 'Choose how the concept work will happen.',
    conceptLabel: 'Concept description',
    conceptPlaceholder: 'What was proposed to the client: solution, scope, what is being worked out next.',
    conceptRequired: 'Describe the concept or attach a Concept artifact.',
    conceptArtifactHint: 'A concept artifact is attached — text is optional.',
    conceptApprovedLabel: 'Client approved the concept',
    conceptNotApproved: 'Not approved yet',
    productTypeLabel: 'Product type',
    productTypePlaceholder: 'Select…',
    productTypeIntegration: 'Industrial Robot',
    productTypeEducational: 'Educational robot',
    productTypeRequired: 'Choose the product type to advance.',
    forkQuestion: 'Does the concept need more work before the TKP is prepared?',
    forkTitle: 'Concept work',
    forkYesConcept: 'Yes, go to concept',
    forkNoTkp: 'No, go to TKP',
    conceptTitle: 'Concept',
    confidenceAvailableFrom: 'Available from the "Feasibility options" stage',
    reasonPlaceholder: 'Why? Free text.',
    remindAtLabel: 'Remind about this deal',
    remindAtHint: 'On this date the deal automatically returns to Validation.',
    reasonOptional: 'Reason (optional)',
    submit: 'Apply',
    advanceTo: 'Advance to {stage}',
    advanceForward: 'Move forward',
    nextStageHint: 'Next stage: {stage}',
    advanceBlockedNoManager: 'Take the inquiry into work first — assign a responsible manager',
    advanceBlockedNoClient: 'Assign a client first',
    changeConfidence: 'Change confidence',
    setConfidenceShort: 'Set',
    rollback: 'Roll back a step',
    rollbackSubmit: 'Roll back',
    rollbackHint: 'Back to the previous stage: {stage}',
    rollbackConfirm: 'The deal will return to the previous stage: {stage}. The card and its history stay intact.',
    rollbackConfirmTerminal: 'The deal will return to the funnel on {stage}. Its outcome will be cleared.',
    rollbackFunnelReturn: 'Back into the funnel: {stage}',
    typeCreated: 'Created',
    typeAdvance: 'Advanced',
    typeSetStage: 'Stage changed',
    typeDrop: 'Dropped',
    typePause: 'Paused',
    typeResume: 'Resumed',
    typeWin: 'Won',
    typeSetConfidence: 'Confidence set',
    typeAssignManager: 'Manager assigned',
    typeAssignClient: 'Client assigned',
    typeAttach: 'File attached',
    typeComment: 'Comment',
    typeSetCriterion: 'Criterion scored',
    typeRollback: 'Rolled back a step',
  },
  criteria: {
    title: 'Verification criteria',
    average: 'Average',
    filled: '{filled} of {total} filled',
    basketGreen: 'Green',
    basketYellow: 'Yellow',
    basketRed: 'Red',
    empty: 'No scores yet — rate the criteria to get a basket suggestion.',
    noteLabel: 'Note',
    notePlaceholder: 'Why this score? (optional)',
    saveNote: 'Save note',
    scoreOption: '{score} of 5',
    toastSaved: 'Score saved',
    toastFailed: 'Failed to save the score',
    open: 'Criteria',
    openEmpty: 'Fill in criteria',
    readOnlyStage: 'Criteria are filled in on the "Verification" stage.',
    historyLine: 'Criterion "{label}": {score}',
    confidenceHint: 'by criteria: {basket}',
  },
  emailTemplates: {
    to: 'To',
    noAddress: 'no address found',
    bodyLabel: 'Letter draft',
    helper: 'The CRM does not send this letter — the manager sends it manually.',
    copy: 'Copy letter',
    copied: 'Copied',
    pause:
      'Hello!\n\nThank you for your inquiry "{summary}". We have paused it for now: {reason}.\n\nWe will get back to you as soon as there is news.\n\nBest regards,\nRobot Factory team',
    drop:
      'Hello!\n\nThank you for your inquiry "{summary}". Unfortunately we cannot take it into work at this time: {reason}.\n\nIf things change, we would be glad to pick the conversation up again.\n\nBest regards,\nRobot Factory team',
  },
  attachments: {
    title: 'Artifacts',
    kindLabel: 'Kind',
    kindConcept: 'Concept',
    kindTkp: 'CP (TKP)',
    kindReport: 'Report',
    kindOther: 'Other',
    fileLabel: 'File',
    chooseFile: 'Choose file',
    noFileChosen: 'No file chosen',
    titleLabel: 'Title',
    titlePlaceholder: 'E.g. Cell concept v1',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'A few words about the contents (optional)',
    upload: 'Upload',
    uploadArtifact: 'Upload artifact',
    modalTitle: 'Upload artifact',
    modalDesc: 'The file will appear in the artifact list and in the inquiry history.',
    download: 'Download',
    empty: 'No artifacts yet.',
    dropHint: 'Drag a file here or press "Upload artifact".',
    tooBig: 'File is larger than 15 MB.',
    toastUploaded: 'File uploaded',
    toastFailed: 'Upload failed',
    edit: 'Edit',
    editTitle: 'Edit artifact',
    delete: 'Delete',
    deleteTitle: 'Delete artifact',
    deleteConfirm: 'Delete "{name}"? This cannot be undone.',
    toastUpdated: 'Artifact updated',
    toastUpdateFailed: 'Update failed',
    toastDeleted: 'Artifact deleted',
    toastDeleteFailed: 'Delete failed',
  },
  dealParams: {
    // Issue #80 — the «Параметры сделки» block of the «Контракт» step.
    title: 'Deal parameters',
    notFilled: 'Not filled in yet',
    // Item 2 — the «Скидка / Полная стоимость» plate: grey until picked.
    plateIdle: 'Discount / Full price',
      modeDiscount: 'Discount',
    modeFullPrice: 'Full price',
    plateChange: 'Change display',
    // Item 1 — positions rows.
    positionsTitle: 'Positions',
    positionRobotType: 'Robot type',
      positionQuantity: 'Qty',
      addPosition: 'Add position',
    removePosition: 'Remove position {row}',
    // Item 3 — «Стоимость контракта».
    costTitle: 'Contract cost',
    totalLabel: 'Total contract cost',
    totalLabelSimple: 'Overall contract cost',
    positionsCountLabel: 'Positions sold',
    positionsCountValue: '{count} pcs.',
    deliveryDateLabel: 'Delivery date',
    robotLabel: 'Robot',
    robotHint: 'Total cost of the “Robot” positions per the contract',
    rtcLabel: 'RTC (robotic complex)',
    rtcHint: 'Final RTC integration cost per the contract — excludes the robots',
    otherLabel: 'Other services cost',
    otherHint: 'Optional — adjacent services outside the RTC scope',
    paymentsTitle: 'Advance / Payment',
    paymentAdvanceLabel: 'Advance',
    paymentPaymentLabel: 'Payment {row}',
    paymentAmount: 'Amount',
    paymentDate: 'Date',
    addPayment: 'Add payment step',
    removePayment: 'Remove payment row {row}',
    // Item 3 check (4.1) — live hint; the server blocks only at WIN.
    sumOk: 'The breakdown adds up to the contract total',
    sumMismatch:
      'Does not add up: the parts give {sum}, the contract total is {total} (difference {diff}). The deal cannot be won with a mismatch.',
    // Items 4–6 — dates.
    shipmentsTitle: 'Shipment date',
    shipmentPosition: 'Shipped position',
    shipmentPositionPlaceholder: 'Pick a position or type your own',
    shipmentDate: 'Shipment date',
    addShipment: 'Add shipment row',
    removeShipment: 'Remove shipment row {row}',
    integrationTitle: 'RTC integration date',
    integrationNotBlocking: 'Optional — does not block the deal.',
    addIntegrationDate: 'Add integration date',
    removeIntegrationDate: 'Remove integration date row {row}',
    closingLabel: 'Deal closing date',
    closingNotBlocking: 'Optional — does not block the deal.',
    // Draft validation (client-side pre-flight; the server re-checks).
    errPositionType: 'Fill in the robot type in every position row.',
    errPositionQuantity: 'Quantity must be a whole number of 1 or more.',
    errPaymentAmount: 'Fill in the amount in every payment row.',
    errShipmentLabel: 'Fill in the position in every shipment row.',
    errIntegrationDate: 'Fill in the date in every integration row (or remove the row).',
  },
  comments: {
    addTitle: 'Add entry',
    fieldTitle: 'Title',
    titlePlaceholder: 'In short: what is this about',
    fieldText: 'Text',
    textPlaceholder: 'Details: agreements, status, next step…',
    submit: 'Add',
    toastAdded: 'Entry added',
    toastFailed: 'Failed to add the entry',
  },
  pipeline: {
    subtitle: 'Commercial block reporting',
    scopeOwnBadge: 'Personal metrics — your own deals only',
    periodLast7Days: 'Last 7 days',
    periodWeek: 'Week',
    periodMonth: 'Month',
    periodQuarter: 'Quarter',
    periodFrom: 'From',
    periodTo: 'To',
    periodApply: 'Apply',
    periodRangeError: 'The "From" date cannot be after the "To" date — check the range.',
    kpiIn: 'Came in',
    kpiAdvanced: 'Done',
    kpiDropped: 'Dropped',
    kpiPaused: 'Paused now',
    kpiConversion: 'Conversion',
    kpiConversionFunnel: 'Funnel conversion',
    kpiConversionFunnelHint:
      'Product of the stage-to-stage conversion rates (stages 1–4) from the report. The Realization pair is excluded: its exit is a separate "win" event.',
    kpiPortfolio: 'Portfolio',
    funnelTitle: 'Value delivery flow',
    funnelNote: 'movement by stage',
    stageIn: 'Came in',
    stageOut: 'Done',
    stageAdvanced: 'moved on',
    stageDropped: 'dropped',
    stagePaused: 'on pause',
    inWork: 'in progress',
    conversionLabel: 'conversion',
    funnelFootnote:
      'Counters: movement during the selected period. “In progress” and stage sums: snapshot at end of period. Stage sums: estimate of the open order portfolio. Wins are counted separately: "moved on" at Realization is always 0 by design.',
    portfolioTitle: 'Project portfolio',
    baseConversionLabel: 'From incoming:',
    portfolioNote: 'portfolio prioritization · open deals at all stages',
    portfolioBalance: 'Portfolio balance:',
    dealsCount: 'Deals: {count}',
    basketShareHint: '{share}% of portfolio sum',
    basketOpen: 'open',
    millionShort: 'M ₽',
    drillInTitle: 'Deals in group',
    drillInScopeNote: 'Deals that entered stages 3–4 during the selected period',
    drillInOfCount: '{shown} of {total} deals',
    drillInEmpty: 'No deals in this group for the period.',
    drillInNoAmount: '—',
    attentionTitle: 'Needs attention',
    attentionNoConfidence: 'Confidence not set',
    attentionStale: 'No movement for {days} d',
    attentionOverdueTasks: 'Overdue tasks',
    attentionAllGood: 'Everything looks fine for this period.',
    attentionNoConfidenceCount:
      '{count} deals at Prioritization/Realization with no confidence set — assign baskets to keep the report honest.',
    attentionPausedCount: '{count} deals currently on pause — review resume dates.',
    attentionDropSpike:
      'At "{stage}" more deals dropped ({dropped}) than moved on ({advanced}) this period.',
    noAccessTitle: 'Reports are manufacturer-only',
    noAccessDesc:
      'The commercial report is available to the manufacturer role. Ask your manufacturer contact for the current numbers.',
    errorTitle: 'Failed to load the report',
    retry: 'Retry',
    emptyStage: 'No deals',
    funnelClickHint: 'click a stage card for drill-in analytics',
    attentionNote: 'auto-hints from the live report',
    sdSummary: 'Summary',
    sdCameIn: 'Came in',
    sdWentOut: 'Done',
    sdOutSplit: 'moved on {advanced} · dropped {dropped} · paused {paused} · returned {returned}',
    sdCurrent: 'Now at stage',
    sdCurrentSum: 'Stage portfolio',
    sdConversion: 'Conversion to next',
    sdAvgAge: 'Avg. age, days',
    sdFromTitle: 'Where they came from',
    sdToTitle: 'Where they went',
    sdFromNew: 'New requests',
    sdFromPause: 'From pause',
    sdToDropped: 'Dropped',
    sdToPaused: 'Paused',
    sdToWon: 'Won',
    sdEmpty: 'No movement in this period.',
    sdTabCurrent: 'Now at stage',
    sdTabIn: 'Came in',
    sdTabOut: 'Done',
    sdTabDropped: 'Dropped',
    sdTabPaused: 'Paused',
    sdAgeDays: '{days} d',
    sdManagerUnset: '—',
  },
  dashboard: {
    title: 'Dashboard',
    subtitleAll: 'Pipeline overview — all inquiries.',
    subtitleAssigned: 'Pipeline overview — your assigned inquiries.',
    kanbanTitle: 'Pipeline kanban arrives in Phase 4',
    kanbanDescription:
      'The inquiry pipeline (NEW → QUALIFYING → QUOTING → BOOKED → WON / REJECTED) will live here. Phase 2 ships the UI system this view is built from.',
    openApiDocs: 'Open API docs',
  },
  placeholder: {
    arrives: '{title} arrives in Phase 4',
    description:
      "Phase 2 ships the UI system; this section's screens come in Phase 4 build.",
  },
  settings: {
    title: 'Settings',
    description: 'Workspace preferences.',
    languageSection: 'Interface language',
    languageLabel: 'Language',
    languageHelper: 'Applied immediately and saved for your user on this device.',
    russian: 'Русский',
    english: 'English',
  },
  inquiries: {
    title: 'Leads',
    descAll: 'Pipeline of sales opportunities — all sources, all integrators.',
    descOwn: 'Inquiries handed over to you.',
    new: 'New Lead',
    emptyTitle: 'No inquiries yet',
    emptyDesc: 'Create the first inquiry to kick off the funnel.',
    modalDesc: 'Capture a sales opportunity. Client and integrator can be assigned later.',
    source: 'Source',
    sourceEndCustomer: 'End-customer',
    sourceIntegrator: 'Integrator',
    sourcePartnerChkpz: 'Partner — ЧКПЗ',
    sourceEmail: 'RF employee',
    channel: 'Channel',
    channelPlaceholder: 'e.g. trade show, LinkedIn, referral from RKM Engineering',
    channelHelper: 'Free-form context on where the inquiry came from.',
    client: 'Client',
    assignClient: 'Assign client',
    assignClientHelper: 'Not linked — find an existing client or create a new one.',
    unassignedOption: '— Unassigned —',
    clientHelper: 'Optional — link to a known client now or later.',
    clientSearchPlaceholder: 'Start typing a company name…',
    clientNoResults: 'No clients found.',
    clientCreateNew: 'Create client “{name}”',
    clientClear: 'Clear client selection',
    clientNewDesc: 'A new client will be created and linked to the inquiry.',
    innPlaceholder: 'e.g. 7706123456',
    innHelper: 'We look the client up by INN, or create a new one with it.',
    summary: 'Summary',
    summaryPlaceholder: 'Brief description of what the customer needs.',
    // Issue #63 — «Card title» (optional; empty falls back to the client name)
    cardTitle: 'Card title',
    cardTitlePlaceholder: 'e.g. Palletising line — Severstal',
    cardTitleHelper: 'Optional. When empty, the client name is shown instead.',
    assignIntegrator: 'Assign integrators for the quote (TKP)',
    // Issue #78 — «General contractor» block (concept step on)
    generalContractorTitle: 'General contractor',
      generalContractorAuto: 'Filled in automatically',
    generalContractorPlaceholder: 'Company name',
    generalContractorPick: 'Pick from integrators',
    tkpArtifactHint: 'TKP uploaded as an artifact',
    assignHelper: 'Pick one or more integrators. Sending them the request is a later stage.',
    tkpIntegratorsPlaceholder: 'Select integrators…',
    tkpIntegratorsChosen: 'Selected: {count}',
    tkpIntegratorsEmpty: 'No active integrators.',
    changeStatus: 'Change inquiry status',
    age: '{age} ago',
    toastMoved: 'Inquiry moved',
    toastCreated: 'Inquiry created',
    toastUpdateFail: 'Could not update inquiry',
    toastCreateFail: 'Could not create inquiry',
    errorUpdate: 'Failed to update inquiry',
    errorCreate: 'Failed to create inquiry',
    // Issue #63 — editing a created inquiry
    edit: 'Edit',
    editTitle: 'Edit inquiry',
    amountPlaceholder: 'e.g. 12500000',
    amountInvalid: 'Enter the amount as a number — e.g. 12500000.',
    toastUpdated: 'Changes saved',
    toastEditFail: 'Could not save the changes',
    errorEdit: 'Could not save the changes',
    viewLabel: 'View',
    viewBoard: 'Kanban',
    viewList: 'List',
    viewActivity: 'By activity',
    viewCalendar: 'Calendar',
    overdueCount: 'Overdue deals: {count}',
    fieldNextStep: 'Next-step date',
    nextStepClear: 'Clear the next-step date',
    searchAria: 'Search inquiries',
    searchPlaceholder: 'Client, RF-id, card title…',
    filterStage: 'Stage',
    filterConfidence: 'Confidence',
    filterIntegrator: 'Integrator',
    filterSource: 'Source',
    filterType: 'Type',
    filterManager: 'Manager',
    typeLabel: 'Request type',
    filterOutcome: 'Outcome',
    allOption: 'All',
    pausedCount: '{count}',
    pausedListLabel: 'Paused deals in this stage',
    colClient: 'Client',
    colSource: 'Source',
    colType: 'Type',
    colSummary: 'Summary',
    colStatus: 'Status',
    colIntegrator: 'Integrator',
    colManager: 'Manager',
    colAmount: 'Amount',
    colCreated: 'Created',
    colAge: 'Age',
    sortNone: 'Not sorted',
    sortAsc: 'Sorted ascending',
    sortDesc: 'Sorted descending',
    closedLabel: 'closed',
    stageProgress: 'Stage {current} of {total}',
    emptyFilteredTitle: 'Nothing matches the filters',
    emptyFilteredDesc: 'Try clearing the search or filters.',
    timelineTitle: 'History',
    timelineEmpty: 'No events yet.',
    drawerDetailsTitle: 'Deal',
    drawerParticipantsTitle: 'Participants',
    drawerActionsTitle: 'Status',
    fieldType: 'Type',
    fieldConfidence: 'Confidence',
    fieldAmount: 'Amount',
    fieldIntegrator: 'Integrator',
    fieldCreatedBy: 'Created by',
    fieldCreated: 'Created',
    fieldClientEmail: 'Client email',
    fieldSourceEmail: 'Client email',
    clientEmail: 'Client email',
    clientEmailPlaceholder: 'name@company.com',
    clientEmailInvalid: 'Check the email format — e.g. name@company.com.',
    confidenceReasonLabel: 'Confidence reason',
    pauseReasonLabel: 'Pause reason',
    pauseRemindLabel: 'Reminder',
    remindUntil: 'until {date}',
    fromPauseBadge: 'From freeze',
    outcomeReasonLabel: 'Outcome reason',
    toastTransition: 'Inquiry updated',
    toastTransitionFail: 'Action failed',
    dndErrRole: 'Only the manufacturer can move deals.',
    dndErrBackward: 'Moving a deal back is not supported.',
    dndErrSkip: 'A deal can be moved only one stage forward.',
    dndErrOffRoute: 'This stage is not part of the route for this product type.',
    dndErrWinStage: 'Only a deal in the Project concept or Commercial proposal stage can be won.',
    listFoot: 'Showing {shown} of {total}',
    boardFootTotal: 'Total in pipeline: {count}',
    boardFootAmount: 'Active work: {amount}',
    openDetails: 'Open inquiry details',
    fieldManager: 'Manager',
    takeIntoWork: 'Take into work',
    taken: 'In work',
    notTaken: 'Not taken',
    toastTaken: 'Inquiry taken into work',
    toastTakeFail: 'Failed to take into work',
    warnNoManager: 'No manager assigned — the inquiry is not taken into work yet. Move it forward anyway?',
  },
  clients: {
    title: 'Clients',
    descAll: 'End-customers in the funnel — all clients across all integrators.',
    descOwn: 'Clients booked to your integrator.',
    add: 'Add client',
    emptyTitle: 'No clients yet',
    emptyDescManufacturer:
      'Create the first client record to start tracking inquiries and bookings.',
    emptyDescIntegrator:
      'Clients appear here once the manufacturer books one to your integrator.',
    colCompany: 'Company',
    colInn: 'Tax ID (INN)',
    colContact: 'Contact',
    colEmail: 'Email',
    colPhone: 'Phone',
    colIndustry: 'Industry',
    colBooking: 'Booking',
    modalTitle: 'Add client',
    modalDesc: 'Create a new end-customer record.',
    fieldCompanyName: 'Company name',
    fieldInn: 'Tax ID (INN)',
    fieldWebsite: 'Website',
    fieldContactName: 'Contact name',
    fieldPosition: 'Position',
    fieldContactEmail: 'Contact email',
    fieldContactPhone: 'Contact phone',
    fieldIndustry: 'Industry',
    fieldNotes: 'Notes',
    bookingHistory: 'Booking history',
    noBookings: 'No bookings yet.',
    colIntegrator: 'Integrator',
    colStatus: 'Status',
    colDate: 'Date',
    toastCreated: 'Client created',
    toastUpdated: 'Client updated',
    toastUpdateFail: 'Could not update client',
    errorUpdate: 'Failed to update client',
    edit: 'Edit',
    editModalTitle: 'Edit client',
    editModalDesc: 'Update the end-customer record.',
    toastCreateFail: 'Could not create client',
    errorCreate: 'Failed to create client',
  },
  contacts: {
    title: 'Contacts',
    add: 'Add contact',
    empty: 'No contacts yet.',
    primary: 'Primary',
    makePrimary: 'Make primary',
    edit: 'Edit contact',
    delete: 'Delete contact',
    deleteConfirm: 'Delete this contact?',
    fieldLastName: 'Last name',
    fieldFirstName: 'First name',
    fieldMiddleName: 'Middle name',
    fieldPhone: 'Phone',
    fieldEmail: 'Email',
    fieldRole: 'Role',
    fieldPositionNote: 'Position note',
    modalAddTitle: 'Add contact',
    modalEditTitle: 'Edit contact',
    toastCreated: 'Contact added',
    toastUpdated: 'Contact updated',
    toastDeleted: 'Contact deleted',
    toastFail: 'Could not save contact',
    roles: {
      SECRETARY: 'Secretary',
      CEO: 'CEO',
      PRODUCTION_DIRECTOR: 'Production director',
      CHIEF_TECHNOLOGIST: 'Chief technologist',
      ENGINEER: 'Engineer',
      OTHER: 'Other',
    },
  },
  orders: {
    title: 'Orders',
    descAll: 'Production pipeline — all integrators.',
    descOwn: 'Your orders.',
    new: 'New order',
    searchAria: 'Search orders',
    searchPlaceholder: 'Search by client, model name, or SKU…',
    emptySearchTitle: 'No orders match your search',
    emptySearchDesc: 'Try a different search term.',
    emptyTitle: 'No orders yet',
    emptyDescManufacturer: 'Create the first order to start the production pipeline.',
    emptyDescIntegrator: 'Create an order for one of your booked clients.',
    colClient: 'Client',
    colIntegrator: 'Integrator',
    colModel: 'Model',
    colQty: 'Qty',
    colUnitPrice: 'Unit price',
    colTotal: 'Total',
    colStatus: 'Status',
    colCreated: 'Created',
    modalTitle: 'New order',
    modalDesc: 'Create an order for a booked client.',
    integratorLabel: 'Integrator',
    selectIntegrator: '— Select integrator —',
    onlyActiveIntegrators: 'Only active integrators.',
    clientLabel: 'Client',
    selectIntegratorFirst: '— Select integrator first —',
    selectClient: '— Select client —',
    clientHelperManufacturer: 'Only clients booked to the selected integrator.',
    clientHelperIntegrator: 'Only clients booked to your integrator.',
    modelLabel: 'Robot model',
    selectModel: '— Select model —',
    modelHelper: "Picking a model auto-fills the unit price; you can override.",
    quantity: 'Quantity',
    unitPriceUsd: 'Unit price (USD)',
    orderTotal: 'Order total',
    changeStatus: 'Change order status',
    clientFallback: 'Client',
    orderFallback: 'Order',
    toastCreated: 'Order created',
    toastStatusUpdated: 'Status updated',
    toastCreateFail: 'Could not create order',
    toastStatusFail: 'Could not update status',
    errorCreate: 'Failed to create order',
    errorStatus: 'Failed to update status',
  },
  bookings: {
    title: 'Bookings',
    descManufacturer: 'Client ↔ integrator assignments. Manufacturer-only create / release.',
    descIntegrator: 'Clients booked to your integrator.',
    book: 'Book client',
    searchAria: 'Search bookings',
    searchPlaceholder: 'Search by client or integrator…',
    filterAria: 'Filter by active state',
    filterActive: 'Active only',
    filterInactive: 'Inactive only',
    filterAll: 'All bookings',
    emptySearchTitle: 'No bookings match your search',
    emptySearchDesc: 'Try a different search term or change the filter.',
    emptyTitle: 'No bookings yet',
    emptyDescManufacturer: 'Book a client to an integrator to start tracking the handoff.',
    emptyDescIntegrator: 'Bookings to your integrator will appear here.',
    colClient: 'Client',
    colIntegrator: 'Integrator',
    colStatus: 'Status',
    colBookedAt: 'Booked at',
    statusReleased: 'Released',
    release: 'Release',
    modalTitle: 'Book client to integrator',
    modalDesc:
      'Assign a client to an integrator. The integrator will then see this client and be able to create orders for them.',
    bookSubmit: 'Book',
    clientLabel: 'Client',
    selectClient: '— Select a client —',
    integratorLabel: 'Integrator',
    selectIntegrator: '— Select an integrator —',
    onlyActiveIntegrators: 'Only active integrators are shown.',
    clientFallback: 'Client',
    integratorFallback: 'Integrator',
    toastCreated: 'Booking created',
    toastReleased: 'Booking released',
    toastCreateFail: 'Could not create booking',
    toastReleaseFail: 'Could not release booking',
    errorCreate: 'Failed to create booking',
    errorRelease: 'Failed to release booking',
  },
  catalog: {
    title: 'Robot catalog',
    descManufacturer: 'Available robot models and base pricing.',
    descIntegrator: 'Available robot models — used when creating orders.',
    add: 'Add model',
    emptyTitle: 'No robot models yet',
    emptyDescManufacturer: 'Add the first SKU to start selling.',
    emptyDescIntegrator: "The manufacturer hasn’t added any models yet.",
    colSku: 'SKU',
    colName: 'Name',
    colDescription: 'Description',
    colBasePrice: 'Base price',
    colStatus: 'Status',
    statusDiscontinued: 'Discontinued',
    modalTitle: 'Add robot model',
    modalDesc: 'Define a new SKU available for orders.',
    fieldSku: 'SKU',
    skuPlaceholder: 'e.g. RF-6AX-PRO',
    fieldName: 'Name',
    fieldBasePriceUsd: 'Base price (USD)',
    fieldDescription: 'Description',
    descriptionPlaceholder: 'Specs, target workloads, distinguishing features.',
    activeLabel: 'Active (available for new orders)',
    toastCreated: 'Robot model created',
    toastCreateFail: 'Could not create model',
    errorCreate: 'Failed to create model',
  },
  integrators: {
    title: 'Integrators',
    description: 'Robot integration partners (RKM).',
    add: 'Add integrator',
    notAvailableTitle: 'Not available',
    notAvailableDesc: 'Integrators are managed by the manufacturer.',
    emptyTitle: 'No integrators yet',
    emptyDesc: 'Create the first integration partner to start booking clients to them.',
    colName: 'Name',
    colEmail: 'Email',
    colPhone: 'Phone',
    colPriority: 'Priority',
    colSpecialization: 'Specialization',
    colStatus: 'Status',
    modalTitle: 'Add integrator',
    modalDesc: 'Register a new integration partner.',
    fieldName: 'Name',
    fieldContactEmail: 'Contact email',
    fieldContactPhone: 'Contact phone',
    fieldPriority: 'Priority',
    priorityHelper: 'Higher number = higher booking preference.',
    fieldSpecialization: 'Specialization',
    specializationPlaceholder: 'e.g. food production, automotive, logistics',
    activeLabel: 'Active (available for new bookings)',
    toastCreated: 'Integrator created',
    toastCreateFail: 'Could not create integrator',
    errorCreate: 'Failed to create integrator',
  },
  notifications: {
    bell: 'Notifications',
    title: 'Notifications',
    empty: 'No notifications yet',
    markAllRead: 'Mark all read',
    unread: '{count} unread',
    unreadOne: 'Unread',
    type: {
      tkpAttached: 'Quote ready',
      managerAssigned: 'Assigned to you',
      stageAdvanced: 'Stage changed',
    },
  },
  components: {
    modal: { close: 'Close dialog' },
    drawer: { close: 'Close drawer' },
    toast: {
      dismiss: 'Dismiss notification',
      region: 'Notifications',
    },
    entityLink: {
      openClient: 'Open client card {name}',
      openIntegrator: 'Open integrator card {name}',
    },
  },
};
